import { participantsToCollaborators } from '@databases/utils/presence/identity-converters';
import { useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl-next';

import { currentUserState } from '@canvas/canvas-state/user';
import { waitForContentAPI } from '@canvas/frame-api/host-api/host-frame-context';
import type { Identity } from '@canvas/model/identity';
import type { ClientId } from '@canvas/model/telepointers';

import { collabStateManager } from './collab-state-manager.ts';

const USER_ARI_PREFIX = 'ari:cloud:identity::user/';
export const usePresenceSync = (contentId: string | null) => {
  const { formatMessage } = useIntl();

  const collabManager = collabStateManager.getCollabManager(contentId ?? '');

  const [participants, setParticipants] = useState<Record<ClientId, Identity>>({});
  const [currentUserClientId, setCurrentUserClientId] = useState<ClientId | null>(null);

  useEffect(() => {
    if (!collabManager) {
      return;
    }

    // Initialize current state
    setParticipants(collabManager.participants);
    setCurrentUserClientId(collabManager.clientId);

    const handleParticipantsUpdate = (newParticipants: Record<ClientId, Identity>) => {
      setParticipants(newParticipants);
    };

    // Set up client ID initialization listener
    const handleClientIdInit = (clientId: ClientId) => {
      setCurrentUserClientId(clientId);
    };

    collabManager.onParticipantsUpdate(handleParticipantsUpdate);
    collabManager.onClientIdInit(handleClientIdInit);

    // Cleanup function
    return () => {
      // Note: CollabManager doesn't provide methods to remove listeners,
      // but they will be cleaned up when the manager is destroyed
    };
  }, [collabManager]);

  // Calculate unique participants without current user
  const uniqueParticipantsWithoutMe: Record<number, Identity> = useMemo(() => {
    if (!contentId || Object.keys(participants).length === 0) {
      return {};
    }

    const participantsMap = Object.entries(participants);

    // Participants always include the current user. Since we rely
    // on "currentUserState", we proceed by removing the current user from the list.
    const participantsWithoutMe = participantsMap.filter(
      ([, identity]) =>
        currentUserState?.identity?.accountId !==
        identity.accountId.replace(USER_ARI_PREFIX, '')
    );

    if (participantsWithoutMe.length === 0) {
      return {};
    }

    // Make participants unique by accountId
    const seenAccountIds = new Set<string>();
    const uniqueParticipants = participantsWithoutMe.filter(([, identity]) => {
      if (seenAccountIds.has(identity.accountId)) {
        return false;
      }
      seenAccountIds.add(identity.accountId);
      return true;
    });

    return Object.fromEntries(uniqueParticipants);
  }, [contentId, participants]);

  useEffect(() => {
    if (!contentId) {
      return;
    }

    const syncCollaboratorsWithHost = async () => {
      try {
        if (currentUserState.status === 'succeeded' && currentUserClientId) {
          const collaborators = await participantsToCollaborators(
            currentUserState.identity,
            currentUserClientId,
            uniqueParticipantsWithoutMe,
            formatMessage
          );
          const contentApi = await waitForContentAPI();
          contentApi.confluenceAPI?.shell?.updateCollaborators?.(collaborators);
        }
      } catch (e) {
        console.error('Failed to sync collaborators:', e);
      }
    };

    syncCollaboratorsWithHost();
  }, [contentId, formatMessage, uniqueParticipantsWithoutMe, currentUserClientId]);
};
